import React, { useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LayoutGrid, Search, ChevronDown, ChevronRight, House, BookOpen, Activity, GraduationCap, CalendarDays, Globe, BedDouble, Settings, Vote, Headset, CreditCard } from 'lucide-react'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import { MEMBER_SECONDARY_ROUTE_LABELS } from '../lib/navigation'
import './MemberCommandBar.css'

export const normaliseMemberSearch = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase()
export const memberRouteMatches = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`)
const icons = { dashboard: House, 'book-facilities': BookOpen, activities: Activity, academy: GraduationCap, events: CalendarDays, 'reciprocal-clubs': Globe, accommodations: BedDouble, manage: Settings, voting: Vote }

/** Desktop browser only. Mounted by MemberLayout's existing native and responsive guards. */
export default function MemberCommandBar({ navItems, clubCode }) {
  const { t } = useOptionalPrivanaI18n()
  const text = key => t(`portalPresentation.memberCommand.${key}`)
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(-1)
  const root = useRef(null), input = useRef(null), trigger = useRef(null)
  const id = useId()
  const close = () => { setOpen(false); setQuery(''); setHighlight(-1) }
  useEffect(close, [location.key])
  useEffect(() => {
    const shortcut = event => {
      if (event.defaultPrevented || event.altKey || !(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'k') return
      if (event.target.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"]') && event.target !== input.current) return
      event.preventDefault()
      setOpen(true)
      input.current?.focus({ preventScroll: true })
    }
    document.addEventListener('keydown', shortcut)
    return () => document.removeEventListener('keydown', shortcut)
  }, [])
  useEffect(() => {
    if (!open) return
    const outside = event => { if (!root.current?.contains(event.target)) close() }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('focusin', outside)
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('focusin', outside) }
  }, [open])
  const destinations = navItems.map(item => {
    const key = item.to.split('/').pop()
    return { ...item, key, title: t(item.labelKey), description: text(key), keywords: text(`${key}Keywords`), icon: icons[key] || LayoutGrid }
  })
  const section = location.pathname.split('/')[2]
  const isFacilityRoute = ['golf', 'book-racquet', 'spa', 'fitness'].includes(section)
  const active = destinations.find(item => memberRouteMatches(location.pathname, item.to)) || (isFacilityRoute ? destinations.find(item => item.key === 'book-facilities') : undefined)
  const current = active?.title || (MEMBER_SECONDARY_ROUTE_LABELS[section] ? t(MEMBER_SECONDARY_ROUTE_LABELS[section]) : text('other'))
  const actions = [
    { key: 'bookings', title: text('bookings'), to: `/${clubCode}/dashboard`, state: { membersBookingDetails: true }, icon: CalendarDays },
    { key: 'account', title: text('account'), to: `/${clubCode}/account`, icon: CreditCard },
    { key: 'desk', title: t('portalPresentation.member_contact_front_desk'), to: `/${clubCode}/contact-front-desk`, state: { membersFrom: `${location.pathname}${location.search}` }, icon: Headset },
  ]
  const matches = item => normaliseMemberSearch(`${item.title} ${item.description || ''} ${item.keywords || ''}`).includes(normaliseMemberSearch(query.trim()))
  const pages = destinations.filter(matches), quick = actions.filter(matches), results = [...pages, ...quick]
  const activate = item => { close(); navigate(item.to, { state: item.state }) }
  const keyDown = event => {
    if (event.key === 'Escape' && open) {
      event.preventDefault(); event.stopPropagation(); close(); trigger.current?.focus({ preventScroll: true }); return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); setOpen(true)
      const next = !results.length ? -1 : highlight < 0 ? (event.key === 'ArrowDown' ? 0 : results.length - 1) : (highlight + (event.key === 'ArrowDown' ? 1 : -1) + results.length) % results.length
      setHighlight(next)
      if (event.target !== input.current) root.current?.querySelector(`[data-result-index="${next}"]`)?.focus({ preventScroll: true })
      const row = root.current?.querySelector(`[data-result-index="${next}"]`)
      const scroller = row?.closest('.mp-command-results')
      if (row && scroller) {
        if (row.offsetTop < scroller.scrollTop) scroller.scrollTop = row.offsetTop
        else if (row.offsetTop + row.offsetHeight > scroller.scrollTop + scroller.clientHeight) scroller.scrollTop = row.offsetTop + row.offsetHeight - scroller.clientHeight
      }
    }
    if (event.key === 'Enter' && open && highlight >= 0 && event.target === input.current && results[highlight]) { event.preventDefault(); activate(results[highlight]) }
  }
  const row = (item, compact = false) => {
    const index = results.indexOf(item), Icon = item.icon
    const isCurrent = !compact && item === active // active is from the same destination list
    return <Link key={item.key} to={item.to} state={item.state} data-result-index={index} onClick={close} onFocus={() => setHighlight(index)} className={`mp-command-row ${compact ? 'mp-command-tile' : ''} ${isCurrent ? 'is-current' : ''} ${highlight === index ? 'is-highlighted' : ''}`} aria-current={isCurrent ? 'page' : undefined}>
      <Icon size={20} aria-hidden="true" /><span><strong>{item.title}</strong>{item.description && <small>{item.description}</small>}</span>{isCurrent && <em>{text('current')}</em>}<ChevronRight size={16} aria-hidden="true" />
    </Link>
  }
  return <div ref={root} className="mp-command" onKeyDown={keyDown}>
    <div className="mp-command-bar">
      <button ref={trigger} type="button" aria-label={text('label')} aria-expanded={open} aria-controls={id} onClick={() => { setOpen(!open); setQuery(''); setHighlight(-1) }}><LayoutGrid size={19} /><span>{current}</span></button>
      <div className="mp-command-search"><Search size={17} aria-hidden="true" /><input ref={input} aria-label={text('placeholder')} aria-controls={id} placeholder={text('placeholder')} value={query} onFocus={() => setOpen(true)} onChange={event => { setQuery(event.target.value); setOpen(true); setHighlight(-1) }} /><kbd>{/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl'} K</kbd></div>
      <button type="button" aria-label={text('open')} aria-expanded={open} aria-controls={id} onClick={() => { if (open) close(); else setOpen(true) }}><ChevronDown size={17} /></button>
    </div>
    {open && <><div className="mp-command-backdrop" aria-hidden="true" /><nav id={id} className="mp-command-panel" aria-label={text('label')}>
      <div className="mp-command-results"><p className="mp-command-announcement" role="status">{text('results').replace('{count}', results.length)}{highlight >= 0 && results[highlight] ? ` · ${results[highlight].title}` : ''}</p>
        {pages.length > 0 && <><h2>{text('club')}</h2>{pages.map(item => row(item))}</>}
        {quick.length > 0 && <div className="mp-command-quick"><h2>{text('quick')}</h2><div>{quick.map(item => row(item, true))}</div></div>}
        {!results.length && <div className="mp-command-empty"><p>{text('empty')}</p><button type="button" onClick={() => { setQuery(''); input.current?.focus({ preventScroll: true }) }}>{text('clear')}</button></div>}
      </div>
      <footer><span>↑ ↓ {text('navigate')}</span><span>Enter {text('open')}</span><span>Esc {text('close')}</span></footer>
    </nav></>}
  </div>
}
